import { caseApiRequest } from './apiClient';
import type { ApiResult } from './types';
import { isCaseRequestUuid } from '../pages/task-case/taskCaseUpdateHelpers';

export type CaseRequestInvolvementRow = {
  id: string;
  sourceType: string;
  userDisplayName: string;
  userName: string;
  allUserRoles: string;
  dateTime: string;
  actionCode: string;
  actionDisplayName: string;
};

export type CaseRequestInvolvementResponse = {
  caseRequestId: string;
  totalCount: number;
  involvementDetails: CaseRequestInvolvementRow[];
};

function str(o: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function asArray(raw: unknown): unknown[] {
  return Array.isArray(raw) ? raw : [];
}

function normalizeInvolvementRow(row: Record<string, unknown>): CaseRequestInvolvementRow {
  return {
    id: str(row, 'id'),
    sourceType: str(row, 'sourceType', 'source_type'),
    userDisplayName: str(row, 'userDisplayName', 'user_display_name'),
    userName: str(row, 'userName', 'user_name'),
    allUserRoles: str(row, 'allUserRoles', 'all_user_roles'),
    dateTime: str(row, 'dateTime', 'date_time'),
    actionCode: str(row, 'actionCode', 'action_code'),
    actionDisplayName: str(row, 'actionDisplayName', 'action_display_name'),
  };
}

function normalizeResponse(raw: unknown): CaseRequestInvolvementResponse {
  if (!raw || typeof raw !== 'object') {
    return { caseRequestId: '', totalCount: 0, involvementDetails: [] };
  }

  const o = raw as Record<string, unknown>;
  const data = o.data && typeof o.data === 'object' && !Array.isArray(o.data) ? (o.data as Record<string, unknown>) : o;
  const involvementDetailsRaw = data.involvement_details ?? data.involvementDetails ?? data.tasks ?? data.rows ?? data.items;
  const involvementDetails = asArray(involvementDetailsRaw)
    .map((item) => (item && typeof item === 'object' ? normalizeInvolvementRow(item as Record<string, unknown>) : null))
    .filter((item): item is CaseRequestInvolvementRow => Boolean(item));
  const caseRequestId = str(data, 'caseRequestId', 'case_request_id');
  const totalCountRaw = data.totalCount ?? data.total_count;
  const totalCount = typeof totalCountRaw === 'number' && Number.isFinite(totalCountRaw) ? totalCountRaw : involvementDetails.length;

  return { caseRequestId, totalCount, involvementDetails };
}

export async function listCaseRequestInvolvement(input: {
  caseRequestId: string;
  signal?: AbortSignal;
  applicationCode?: string;
}): Promise<ApiResult<CaseRequestInvolvementResponse>> {
  const id = input.caseRequestId.trim();
  if (!isCaseRequestUuid(id)) return { success: false, message: 'Invalid case request id.' };

  const applicationCode = (input.applicationCode ?? 'INTRANET').trim();

  const res = await caseApiRequest<unknown>(`/${encodeURIComponent(id)}/involvement-details`, {
    method: 'GET',
    signal: input.signal,
    headers: { applicationCode, 'application-code': applicationCode },
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: normalizeResponse(res.data) };
}